/**
 * Client for the customer's data model on top of the DIN 276 — /:customer/data-model/* on
 * tri-server. Reading is open to the tenant, writing needs an administrator of it.
 */

import type { TenantAttributeSpec } from "@the-real-insight/tri-model";

import { configuration } from "../../../Configuration";
import { ApplicationError, UnauthorizedError } from "../../../general/Errors";
import { authenticatedFetch } from "../../../JWTAuthService";
import { userAPI } from "../../../UserAPI";
import type { TenantAttribute } from "./dataModel";

const processResponse = async (response: Response) => {
  if (response.ok) return response.json();
  if (response.status === 401) throw new UnauthorizedError();

  let errorMessage = `${response.status} ${response.statusText}`;
  try {
    const errorData = await response.json();
    errorMessage = errorData.message || errorData.error || errorMessage;
  } catch {
    // response body not JSON — keep default message
  }
  throw new ApplicationError(errorMessage);
};

/** Why an attribute was not added to a class of the selection. */
export type SkipReason = "unknownClass" | "builtin" | "duplicate";

export interface CreateResult {
  created: TenantAttribute[];
  skipped: { classCode: string; reason: SkipReason }[];
}

const idOf = (value: any): string => String(value?.$oid ?? value);
const normalizeAttribute = (raw: any): TenantAttribute => ({ ...raw, _id: idOf(raw._id) });

export const useDataModelAPI = (customer: string) => {
  const url = `${configuration.serverUrl}/${customer}/data-model/attributes`;

  const send = async (method: "GET" | "POST" | "PUT" | "DELETE", body?: unknown) => {
    const result = await processResponse(
      await authenticatedFetch(url, {
        method,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        headers: { "Content-Type": "application/json" },
      }),
    );
    // The classes the rest of the portal reads carry these attributes — they are read anew.
    if (method !== "GET") userAPI.invalidateDIN276Metadata();
    return result;
  };

  return {
    getAttributes: async (): Promise<TenantAttribute[]> =>
      ((await send("GET"))?.definitions ?? []).map(normalizeAttribute),
    createAttribute: async (classCodes: string[], spec: TenantAttributeSpec): Promise<CreateResult> => {
      const result = await send("POST", { classCodes, spec });
      return { created: (result?.created ?? []).map(normalizeAttribute), skipped: result?.skipped ?? [] };
    },
    updateAttributes: async (ids: string[], spec: TenantAttributeSpec): Promise<TenantAttribute[]> =>
      ((await send("PUT", { ids, spec }))?.definitions ?? []).map(normalizeAttribute),
    deleteAttributes: async (ids: string[]): Promise<number> => (await send("DELETE", { ids }))?.deleted ?? 0,
  };
};
